import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Card, Spinner, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import PropTypes from 'prop-types';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';
import ModalAgregarProductoSuscripcion from '../suscripciones/Modales/ModalAgregarProductoSuscripcion';

const SeccionModulosLicenciaCliente = ({ idLicencia, infoSuscripcion }) => {
    const { token } = useToken();
    const { showErrorToast } = useToast();

    const [modulos, setModulos] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [showModal, setShowModal] = useState(false);

    // Carga los módulos asociados a la licencia
    const cargarModulos = useCallback(async () => {
        if (!idLicencia) return;
        setCargando(true);
        try {
            const resp = await fetch(`/api/licencia/ObtenerModulosPorLicencia?idLicencia=${idLicencia}`, {
                method: 'GET',
                cache: 'no-store',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                }
            });
            const data = await resp.json();
            if (data.iResponseType === 1 && data.detalles) {
                const det = JSON.parse(data.detalles);
                setModulos(Array.isArray(det?.table2) ? det.table2 : []);
            } else {
                setModulos([]);
            }
        } catch {
            showErrorToast('Error al cargar módulos de la licencia');
        } finally {
            setCargando(false);
        }
    }, [idLicencia, token]);

    useEffect(() => {
        cargarModulos();
    }, [cargarModulos]);

    const getEstadoModulo = m => {
        if (typeof m?.bIsActive === 'boolean') return m.bIsActive;
        if (m?.bIsActive === 1 || m?.bIsActive === '1') return true;
        if (m?.bIsActive === 0 || m?.bIsActive === '0') return false;

        // Fallback para data histórica: fecha fin futura = activo
        if (!m?.dFechaFin) return false;
        const fechaFin = new Date(m.dFechaFin);
        return !Number.isNaN(fechaFin.getTime()) && fechaFin > new Date();
    };

    const formatearFechaAsignacion = fecha => {
        const parsed = new Date(fecha);
        if (Number.isNaN(parsed.getTime())) return '';
        return parsed.toLocaleDateString('es-MX', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    return (
        <>
            <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                        <FontAwesomeIcon
                            icon={icon({ name: 'puzzle-piece', style: 'solid' })}
                            className="me-2 text-primary"
                        />
                        Módulos de Suscripción
                    </h5>
                    {infoSuscripcion && (
                        <Button variant="primary" size="sm" onClick={() => setShowModal(true)}>
                            <FontAwesomeIcon icon={icon({ name: 'plus', style: 'solid' })} className="me-1" />
                            Agregar Módulo
                        </Button>
                    )}
                </Card.Header>

                <Card.Body className="p-0">
                    {cargando ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" variant="primary" />
                        </div>
                    ) : (
                        <Table responsive hover className="mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th className="text-muted fw-bold">#</th>
                                    <th className="text-muted fw-bold">Módulo</th>
                                    <th className="text-muted fw-bold">Producto</th>
                                    <th className="text-muted fw-bold">Renovación</th>
                                    <th className="text-muted fw-bold">Estado</th>
                                    <th className="text-muted fw-bold">Asignado por</th>
                                </tr>
                            </thead>
                            <tbody>
                                {modulos.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} className="text-center text-muted py-4">
                                            Sin módulos asignados.
                                        </td>
                                    </tr>
                                ) : (
                                    modulos.map((m, idx) => {
                                        const isActive = getEstadoModulo(m);
                                        const idModulo = m.idModulosLicenciaCliente ?? m.idModulo ?? idx;

                                        return (
                                        <tr key={idModulo}>
                                            <td>{idModulo}</td>
                                            <td>{m.vDescripcionModulo || '—'}</td>
                                            <td>{m.vNombreProducto || '—'}</td>
                                            <td>{m.vDescripcion || infoSuscripcion?.vTipoRenovacion || '—'}</td>
                                            <td>
                                                <Badge bg={isActive ? 'success' : 'secondary'}>
                                                    {isActive ? 'Activo' : 'Inactivo'}
                                                </Badge>
                                            </td>
                                            <td className="text-muted">
                                                {m.vCreatedUser}
                                                {m.dCreatedDate
                                                    ? ` · ${formatearFechaAsignacion(m.dCreatedDate)}`
                                                    : ''}
                                            </td>
                                        </tr>
                                    )})
                                )}
                            </tbody>
                        </Table>
                    )}
                </Card.Body>
            </Card>

            <ModalAgregarProductoSuscripcion
                show={showModal}
                onHide={() => setShowModal(false)}
                suscripcion={infoSuscripcion ? { ...infoSuscripcion, idLicencia: infoSuscripcion.idLicencia ?? idLicencia } : null}
                onCreated={cargarModulos}
            />
        </>
    );
};

SeccionModulosLicenciaCliente.propTypes = {
    idLicencia: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    idClienteAkasia: PropTypes.number,
    infoSuscripcion: PropTypes.object
};

export default SeccionModulosLicenciaCliente;
